/**
 * API Client Configuration
 * Axios instance with JWT token handling
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API base URL - auto-detect based on how the app is accessed
export function getApiBaseUrl(): string {
  const hostname = window.location.hostname;
  // Public access via classrooms.work → use api.classrooms.work
  if (hostname === 'classrooms.work' || hostname === 'www.classrooms.work') {
    return 'https://api.classrooms.work/api';
  }
  // LAN or localhost → use env var or default
  return process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
}
const API_BASE_URL = getApiBaseUrl();

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds (for video uploads)
});

// Request interceptor - add JWT token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle 401 Unauthorized - redirect to login
      if (error.response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      // Handle 403 Forbidden
      if (error.response.status === 403) {
        console.error('Access denied:', error.response.data);
      }

      // Handle 500 Internal Server Error
      if (error.response.status === 500) {
        console.error('Server error:', error.response.data);
      }
    }

    return Promise.reject(error);
  }
);

// Base URL for static assets (videos, thumbnails) — no /api suffix
export function getStaticBaseUrl(): string {
  const hostname = window.location.hostname;
  if (hostname === 'classrooms.work' || hostname === 'www.classrooms.work') {
    return 'https://api.classrooms.work';
  }
  return process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8001';
}

export default apiClient;
